import MapboxViewer from "@/components/mapbox/MapboxViewer";
import { autobind } from "core-decorators";
import { MapboxCustomObject } from "./MapboxCustomObject";
import type { FeatureMeasureAreaData } from "@/types/content";
import * as mapboxgl from "mapbox-gl";
import { MAPBOX_MIN_ZOOM } from "@/constant";
export class MapboxMeasureAreaObject extends MapboxCustomObject {
  private _segmentLabelsSourceId: string | null = null;
  private _segmentLabelsLayerId: string | null = null;

  constructor(mapboxViewer: MapboxViewer, content: FeatureMeasureAreaData) {
    super(mapboxViewer, content);
    this._contentId = content.id;
    this._editor = this._mapboxViewer.editors.editorMeasureAreaController!;
    this.init(content);
  }

  set visible(visible: boolean) {
    this._content.config.visible = visible;
    this._mapboxViewer.entities.polygonEntity?.updateSource();
  }

  get visible() {
    return this._content.config.visible;
  }

  @autobind
  init(content: FeatureMeasureAreaData) {
    this._content = content;
    this._contentId = content.id;
    // Add to shared entity
    this._mapboxViewer.entities.measureAreaEntity?.add(this);

    // Create segment labels layer
    this._segmentLabelsSourceId = `${this._contentId}-segments-source`;
    this._segmentLabelsLayerId = `${this._contentId}-segments-layer`;

    // Add source and layer for segment labels
    if (content.coordinates && content.coordinates.length > 1) {
      const segmentLabels = this.getSegmentLabels(content.coordinates);

      this._mapboxViewer._mapViewer.addSource(this._segmentLabelsSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        },
        minzoom: MAPBOX_MIN_ZOOM,
      });

      this._mapboxViewer._mapViewer.addLayer({
        id: this._segmentLabelsLayerId,
        type: "symbol",
        source: this._segmentLabelsSourceId,
        layout: {
          "text-field": ["get", "length"],
          "text-size": 12,
          "text-anchor": "center",
          "text-offset": [0, -0.5],
          visibility: content.config?.visible ? "visible" : "none",
        },
        paint: {
          "text-color": content.config?.color || "#4ecdc4",
          "text-halo-color": "#ffffff",
          "text-halo-width": 2,
        },
      });
    }
  }

  @autobind
  getFeature(): GeoJSON.Feature | null {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return null;
    }

    const content = this._content as FeatureMeasureAreaData;

    return {
      type: "Feature",
      id: this._contentId,
      geometry: {
        type: "Polygon",
        coordinates: [this._content.coordinates],
      },
      properties: {
        contentId: this._contentId,
        app: "logistic-app",
        color: content.config.color || "#4ecdc4",
        name: `Area: ${this.formatArea(content.properties.area)}`,
        category: "measurement_area",
        area: content.properties.area,
        unit: content.properties.unit,
        visible: content.config.visible,
      },
    };
  }

  @autobind
  getLabelFeatures(): GeoJSON.Feature[] {
    if (!this._content.coordinates || this._content.coordinates.length < 3) {
      return [];
    }

    const content = this._content as FeatureMeasureAreaData;
    const features: GeoJSON.Feature[] = [];

    // Add vertex points for each corner (excluding the closing point)
    for (let i = 0; i < this._content.coordinates.length - 1; i++) {
      const coord = this._content.coordinates[i];
      features.push({
        type: "Feature",
        id: `${this._contentId}-vertex-${i}`,
        geometry: {
          type: "Point",
          coordinates: coord,
        },
        properties: {
          app: "logistic-app",
          measureId: this._contentId,
          vertexType: "corner",
          vertexIndex: i,
          category: "measure_label",
          visible: content.config.visible,
        },
      });
    }

    // Add midpoint vertices for editing
    for (let i = 0; i < this._content.coordinates.length - 1; i++) {
      const current = this._content.coordinates[i];
      const next =
        this._content.coordinates[
          (i + 1) % (this._content.coordinates.length - 1)
        ];
      const midpoint = [(current[0] + next[0]) / 2, (current[1] + next[1]) / 2];

      features.push({
        type: "Feature",
        id: `${this._contentId}-midpoint-${i}`,
        geometry: {
          type: "Point",
          coordinates: midpoint,
        },
        properties: {
          app: "logistic-app",
          measureId: this._contentId,
          vertexType: "midpoint",
          insertIndex: i + 1,
          category: "measure_label",
          visible: content.config.visible,
        },
      });
    }

    // Add area label at the centroid of the polygon
    const centroid = this.calculateCentroid(this._content.coordinates);
    features.push({
      type: "Feature",
      id: `${this._contentId}-area-label`,
      geometry: {
        type: "Point",
        coordinates: centroid,
      },
      properties: {
        app: "logistic-app",
        measureId: this._contentId,
        labelType: "area",
        area: content.properties.area,
        areaFormatted: this.formatArea(content.properties.area),
        unit: content.properties.unit,
        category: "measure_label",
        visible: content.config.visible,
      },
    });

    return features;
  }

  @autobind
  updateContent(content: FeatureMeasureAreaData) {
    this._content = content;
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();

    // Update segment labels
    if (
      this._segmentLabelsSourceId &&
      content.coordinates &&
      content.coordinates.length > 1
    ) {
      const segmentLabels = this.getSegmentLabels(content.coordinates);
      const source = this._mapboxViewer._mapViewer.getSource(
        this._segmentLabelsSourceId
      ) as mapboxgl.GeoJSONSource;

      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: segmentLabels.map((label) => ({
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: label.coordinates,
            },
            properties: {
              length: label.length,
            },
          })),
        });
      }

      // Update layer visibility and color
      if (this._segmentLabelsLayerId) {
        this._mapboxViewer._mapViewer.setLayoutProperty(
          this._segmentLabelsLayerId,
          "visibility",
          content.config?.visible ? "visible" : "none"
        );
        this._mapboxViewer._mapViewer.setPaintProperty(
          this._segmentLabelsLayerId,
          "text-color",
          content.config?.color || "#4ecdc4"
        );
      }
    }
  }

  @autobind
  dispose() {
    // Remove segment labels layer and source
    if (
      this._segmentLabelsLayerId &&
      this._mapboxViewer._mapViewer.getLayer(this._segmentLabelsLayerId)
    ) {
      this._mapboxViewer._mapViewer.removeLayer(this._segmentLabelsLayerId);
    }
    if (
      this._segmentLabelsSourceId &&
      this._mapboxViewer._mapViewer.getSource(this._segmentLabelsSourceId)
    ) {
      this._mapboxViewer._mapViewer.removeSource(this._segmentLabelsSourceId);
    }
    this._segmentLabelsSourceId = null;
    this._segmentLabelsLayerId = null;

    this._mapboxViewer.entities.measureAreaEntity?.remove(this._contentId);
  }

  @autobind
  setEditing(editing: boolean) {
    // Update editing state and trigger source update
    if (editing) {
      // Enable editing mode - show vertices
      this._mapboxViewer.entities.measureAreaEntity?.showVertices();
    } else {
      // Disable editing mode - hide vertices
      this._mapboxViewer.entities.measureAreaEntity?.hideVertices();
    }
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();
  }

  @autobind
  setDragHighlight(highlighted: boolean) {
    // Update feature state for visual feedback
    this._mapboxViewer._mapViewer.setFeatureState(
      { source: "measure-source", id: this._contentId },
      { dragging: highlighted }
    );
  }

  @autobind
  moveMeasure(newCoordinates: number[][]) {
    // Temporarily update coordinates for real-time feedback
    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();
  }

  @autobind
  updateVertex(vertexIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length - 1 // Exclude closing point for polygons
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates[vertexIndex] = newCoordinate;

    // Update the closing point if we're moving the first vertex
    if (vertexIndex === 0) {
      newCoordinates[newCoordinates.length - 1] = newCoordinate;
    }

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();
  }

  @autobind
  insertVertex(insertIndex: number, newCoordinate: number[]) {
    if (
      !this._content.coordinates ||
      insertIndex < 0 ||
      insertIndex > this._content.coordinates.length - 1 // Don't insert after closing point
    ) {
      return;
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(insertIndex, 0, newCoordinate);

    // Update the closing point to match the first point
    newCoordinates[newCoordinates.length - 1] = newCoordinates[0];

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();
  }

  @autobind
  removeVertex(vertexIndex: number) {
    if (
      !this._content.coordinates ||
      this._content.coordinates.length <= 4 || // Minimum 3 points + closing point
      vertexIndex < 0 ||
      vertexIndex >= this._content.coordinates.length - 1 // Don't remove closing point
    ) {
      return; // Don't allow removal if it would make an invalid polygon
    }

    const newCoordinates = [...this._content.coordinates];
    newCoordinates.splice(vertexIndex, 1);

    // Update the closing point
    newCoordinates[newCoordinates.length - 1] = newCoordinates[0];

    this._content.coordinates = newCoordinates;
    this._mapboxViewer.entities.measureAreaEntity?.updateSource();
  }

  private formatArea(area: number): string {
    return `${area.toFixed(2)} m²`;
  }

  private calculateCentroid(coordinates: number[][]): number[] {
    let x = 0;
    let y = 0;
    const n = coordinates.length - 1; // Exclude the closing point

    for (let i = 0; i < n; i++) {
      x += coordinates[i][0];
      y += coordinates[i][1];
    }

    return [x / n, y / n];
  }

  private calculateDistance(
    point1: [number, number],
    point2: [number, number]
  ): number {
    const R = 6371000; // Earth's radius in meters
    const lat1 = (point1[1] * Math.PI) / 180;
    const lat2 = (point2[1] * Math.PI) / 180;
    const deltaLat = ((point2[1] - point1[1]) * Math.PI) / 180;
    const deltaLng = ((point2[0] - point1[0]) * Math.PI) / 180;

    const a =
      Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) *
        Math.cos(lat2) *
        Math.sin(deltaLng / 2) *
        Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  private formatLength(length: number): string {
    if (length >= 1000) {
      return `${(length / 1000).toFixed(2)} km`;
    } else {
      return `${length.toFixed(2)} m`;
    }
  }

  private getSegmentLabels(
    coords: number[][]
  ): { coordinates: [number, number]; length: string }[] {
    const labels: { coordinates: [number, number]; length: string }[] = [];

    for (let i = 0; i < coords.length - 1; i++) {
      const start = coords[i] as [number, number];
      const end = coords[i + 1] as [number, number];
      const length = this.calculateDistance(start, end);

      // Calculate midpoint for label placement
      const midpoint: [number, number] = [
        (start[0] + end[0]) / 2,
        (start[1] + end[1]) / 2,
      ];

      labels.push({
        coordinates: midpoint,
        length: this.formatLength(length),
      });
    }

    return labels;
  }
  @autobind
  focusOn() {
    const coordinates = this._content.coordinates;
    if (!coordinates || coordinates.length === 0) return;

    const bounds = coordinates.reduce((bounds, coord) => {
      return bounds.extend(coord as mapboxgl.LngLatLike);
    }, new mapboxgl.LngLatBounds());
    this._mapboxViewer._mapViewer.fitBounds(bounds);
  }

  @autobind
  setHighlight() {
    // this._mapboxViewer._mapViewer.setFeatureState(
    //   { source: "measure-source", id: this._contentId },
    //   { dragging: true }
    // );
  }
}
